// Phase C · Blocco UNICO operativo "Dettaglio misure (N)" per una voce SAL a misura.
// Fonte autoritativa dell'avanzamento: le SOTTORIGHE. Il padre è derivato/read-only.
// - Eseguito editabile inline (righe non fail-closed) + "Salva avanzamento" (measurement_execution).
// - Azioni per riga: Diminuisci / Aumenta (motori certificati) + "Aggiungi nuova misura".
import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Ruler, Save, TrendingDown, TrendingUp, PlusCircle, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { salApi } from '../../lib/api';
import { toast } from 'sonner';
import { fmtQty, parseNum } from '../../lib/formatQty';
import { MeasureRowVariationDialog } from './MeasureRowVariationDialog';
import { NewMeasureLineDialog } from './NewMeasureLineDialog';

const rowStatus = (r, failClosed) => {
  if (failClosed) return { label: 'Storico', cls: 'bg-amber-100 text-amber-700' };
  const cur = Number(r.current_quantity) || 0;
  const exe = Number(r.executed_quantity) || 0;
  const orig = Number(r.original_calculated_quantity) || 0;
  if (cur === 0 && orig > 0) return { label: 'Stralciata', cls: 'bg-rose-100 text-rose-700' };
  if (exe === 0 && cur > 0) return { label: 'Da eseguire', cls: 'bg-slate-100 text-slate-600' };
  if (exe > 0 && exe < cur) return { label: 'In corso', cls: 'bg-blue-100 text-blue-700' };
  if (exe >= cur && cur > 0) return { label: 'Completata', cls: 'bg-emerald-100 text-emerald-700' };
  return { label: '—', cls: 'bg-slate-100 text-slate-600' };
};

const dimsTooltip = (r) => {
  const parts = [r.current_parts ?? r.parts, r.current_length ?? r.length, r.current_width ?? r.width, r.current_height_weight ?? r.height_weight]
    .filter((v) => v != null && v !== '').map((v) => fmtQty(v));
  return parts.length ? `Misure: ${parts.join(' × ')}` : 'Quantità manuale';
};

export const SalMeasureDetail = ({ item, canEdit = false, onSaved }) => {
  const lines = Array.isArray(item?.sal_measurement_lines) ? item.sal_measurement_lines : [];
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const itemFailClosed = item?.executed_reconstruction === 'fail_closed_legacy';

  const sorted = useMemo(() => [...lines].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)), [lines]);
  const [exec, setExec] = useState(() => Object.fromEntries(sorted.map((r) => [r.source_measurement_id, String(r.executed_quantity ?? 0)])));

  const [varDlg, setVarDlg] = useState({ open: false, row: null, mode: 'decrease' });
  const [addOpen, setAddOpen] = useState(false);

  // Re-sync local executed state if backend lines change (after save/refresh)
  const linesKey = sorted.map((r) => `${r.source_measurement_id}:${r.executed_quantity}:${r.current_quantity}`).join('|');
  React.useEffect(() => {
    setExec(Object.fromEntries(sorted.map((r) => [r.source_measurement_id, String(r.executed_quantity ?? 0)])));
  }, [linesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (lines.length === 0) return null;

  const isRowFailClosed = (r) => itemFailClosed || r.executed_reconstructed === false;

  const saveExecuted = async () => {
    setSaving(true);
    try {
      const measurement_execution = sorted
        .filter((r) => !isRowFailClosed(r))
        .map((r) => ({ source_measurement_id: r.source_measurement_id, executed_quantity: parseNum(exec[r.source_measurement_id]) || 0 }));
      await salApi.update(item.id, { sal_accounting_mode: 'quantity', measurement_execution });
      toast.success('Avanzamento salvato');
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || (typeof detail === 'string' ? detail : 'Errore nel salvataggio avanzamento'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2" data-testid={`sal-measure-detail-${item.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs font-medium text-[#0078C8] hover:underline"
        data-testid={`sal-measure-toggle-${item.id}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Ruler className="w-3.5 h-3.5" />
        Dettaglio misure ({sorted.length})
      </button>

      {open && (
        <div className="mt-2 rounded-md border border-[#EDEDED]" data-testid={`sal-measure-table-${item.id}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[640px]">
              <thead>
                <tr className="bg-slate-50 text-[#737373] text-left">
                  <th className="px-2 py-1.5 font-semibold">Descrizione</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Originale</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Corrente</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Eseguito</th>
                  <th className="px-2 py-1.5 font-semibold text-right">Residuo</th>
                  <th className="px-2 py-1.5 font-semibold">Stato</th>
                  {canEdit && <th className="px-2 py-1.5 font-semibold text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const failClosed = isRowFailClosed(r);
                  const st = rowStatus(r, failClosed);
                  const cur = Number(r.current_quantity) || 0;
                  const exeVal = failClosed ? null : (parseNum(exec[r.source_measurement_id]) || 0);
                  const residual = failClosed ? null : Math.max(0, cur - exeVal);
                  return (
                    <tr key={r.source_measurement_id} className="border-t border-[#F1F1F1]" data-testid={`sal-measure-row-${r.source_measurement_id}`}>
                      <td className="px-2 py-1.5 text-[#3C3C3C]" title={dimsTooltip(r)}>
                        {r.description || '—'}
                        {r.source === 'extra' && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-[#fff3ea] text-[#FF7819]">EXTRA</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtQty(r.original_calculated_quantity)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtQty(r.current_quantity)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">
                        {failClosed ? (
                          <span className="text-amber-700" title="Eseguito storico non attribuibile">—</span>
                        ) : canEdit ? (
                          <Input type="text" inputMode="decimal" value={exec[r.source_measurement_id] ?? '0'}
                            onChange={(e) => setExec((p) => ({ ...p, [r.source_measurement_id]: e.target.value }))}
                            className="w-20 h-7 text-xs input-abitalo text-right ml-auto"
                            data-testid={`sal-measure-exec-input-${r.source_measurement_id}`} />
                        ) : fmtQty(r.executed_quantity)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtQty(residual)}</td>
                      <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${st.cls}`} data-testid={`sal-measure-status-${r.source_measurement_id}`}>{st.label}</span></td>
                      {canEdit && (
                        <td className="px-2 py-1.5">
                          <div className="flex items-center justify-end gap-1">
                            <button type="button" title="Diminuisci" onClick={() => setVarDlg({ open: true, row: r, mode: 'decrease' })}
                              className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`sal-measure-decrease-${r.source_measurement_id}`}>
                              <TrendingDown className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" title="Aumenta" onClick={() => setVarDlg({ open: true, row: r, mode: 'increase' })}
                              className="p-1 rounded hover:bg-emerald-50 text-emerald-700" data-testid={`sal-measure-increase-${r.source_measurement_id}`}>
                              <TrendingUp className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(itemFailClosed || sorted.some((r) => r.executed_reconstructed === false)) && (
            <div className="px-2 py-1.5 text-[10px] text-amber-700 bg-amber-50 border-t border-amber-100">
              Eseguito storico non attribuibile per-sottoriga: l'avanzamento resta valido a livello di voce.
            </div>
          )}

          {canEdit && (
            <div className="flex items-center justify-between gap-2 px-2 py-2 border-t border-[#F1F1F1] bg-[#fafafa]">
              <Button size="sm" variant="outline" className="h-7 border-[#0078C8] text-[#0078C8] hover:bg-[#eef7fd]"
                onClick={() => setAddOpen(true)} data-testid={`sal-measure-add-${item.id}`}>
                <PlusCircle className="w-3.5 h-3.5 mr-1" /> Aggiungi nuova misura
              </Button>
              <Button size="sm" className="h-7 bg-[#FF7819] hover:bg-[#e56a13]" onClick={saveExecuted} disabled={saving || itemFailClosed}
                data-testid={`sal-measure-save-${item.id}`}>
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Salva avanzamento
              </Button>
            </div>
          )}
        </div>
      )}

      {varDlg.row && (
        <MeasureRowVariationDialog open={varDlg.open} onOpenChange={(v) => setVarDlg((p) => ({ ...p, open: v }))}
          item={item} row={varDlg.row} mode={varDlg.mode} onDone={onSaved} />
      )}
      <NewMeasureLineDialog open={addOpen} onOpenChange={setAddOpen} item={item} onDone={onSaved} />
    </div>
  );
};

export default SalMeasureDetail;
